import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2,
  Upload,
  RotateCcw,
  BrainCircuit,
  Download,
  ChevronDown,
} from "lucide-react";
import {
  useExtractLightingData,
  useExtractTemperatureData,
  useExtractNoiseDoseData,
  useRequestTempPresignedUrls,
} from "@/hooks";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RequestTempPresignedUrlsResponse } from "@/api";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { pdf2pngs } from "@/lib/utils";
import { useState } from "react";
import { toast } from "sonner";
import { useUserDetails } from "@/hooks";
import { Navigate } from "react-router";
import { z } from "zod";
import { usePutPresignedUrls } from "@/hooks/use-put-presigned-urls";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDropzone } from "react-dropzone";
import posthog from "posthog-js";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ExtractDataForm = z.object({
  type: z.enum(["lighting", "temperature", "noise-dose"], {
    message: "Tiene que seleccionar un tipo agente",
  }),
  fileInput: z.array(z.instanceof(File)).min(1, "Se requiere al menos un archivo"),
});

type ExtractDataForm = z.infer<typeof ExtractDataForm>;

export default function ExtractDataPage() {
  const { data: user } = useUserDetails();

  const { mutateAsync: extractLightingData } = useExtractLightingData();
  const { mutateAsync: extractTemperatureData } = useExtractTemperatureData();
  const { mutateAsync: extractNoiseDoseData } = useExtractNoiseDoseData();

  const { mutateAsync: requestTempPresignedUrls } =
    useRequestTempPresignedUrls();
  const { mutateAsync: putPresignedUrl } = usePutPresignedUrls();

  const [isProcessing, setIsProcessing] = useState(false);
  const [filesProcessed, setFilesProcessed] = useState(0);
  const [filesToProcess, setFilesToProcess] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);

  const form = useForm<ExtractDataForm>({
    resolver: zodResolver(ExtractDataForm),
    defaultValues: {
      fileInput: [],
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    multiple: true,
    onDrop: async (acceptedFiles) => {
      const files = await processFiles(acceptedFiles);
      form.setValue(
        "fileInput",
        files.concat(form.getValues("fileInput") ?? [])
      );
    },
  });

  if (user?.type === "anonymous") {
    return <Navigate to="/inicia-sesion?redirect=capturar-datos" />;
  }

  const processFiles = async (files: FileList | File[]) => {
    setIsProcessing(true);
    setFilesProcessed(0);
    setFilesToProcess(files.length);

    const empty: File[] = [];
    const processedFiles: File[] = [];

    for (const file of files) {
      if (file.type === "application/pdf") {
        try {
          const images = await pdf2pngs(file);
          processedFiles.push(...images);
        } catch (error) {
          console.error("Error converting PDF:", error);
          toast.error("Error al convertir el PDF", {
            description:
              "Por favor, asegúrate de que el archivo sea un PDF válido.",
          });
          setIsProcessing(false);
          setFilesProcessed(0);
          setFilesToProcess(0);
          return empty;
        }
      }
      if (
        file.type === "image/png" ||
        file.type === "image/jpeg" ||
        file.type === "image/jpg"
      ) {
        processedFiles.push(file);
      }
      setFilesProcessed((prev) => prev + 1);
    }

    setTimeout(() => {
      setIsProcessing(false);
      setFilesProcessed(0);
      setFilesToProcess(0);
    }, 500);

    return processedFiles;
  };

  const incProgress = async () => {
    return setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev < 100) {
          const result = prev + Math.random() * 3;
          return Math.min(100, result);
        }
        return Math.min(100, prev);
      });
    }, 3000);
  };

  const onSubmit: SubmitHandler<ExtractDataForm> = async (data) => {
    setIsAnalyzing(true);
    setAnalysisProgress(0);

    if (
      data.fileInput[0].type !== "image/png" &&
      data.fileInput[0].type !== "image/jpeg" &&
      data.fileInput[0].type !== "image/jpg"
    ) {
      toast.error("Error al convertir el PDF", {
        description:
          "Por favor, asegúrate de que el archivo sea un PNG, JPG o JPEG válido.",
      });
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      return;
    }

    if (!data?.fileInput?.length) {
      toast.error("No se han seleccionado archivos", {
        description: "Por favor, selecciona al menos un archivo.",
      });
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      return;
    }

    let urls: RequestTempPresignedUrlsResponse[] = [];
    try {
      urls = await requestTempPresignedUrls({ amount: data.fileInput.length });

      const uploads = await Promise.all(
        data.fileInput.map(async (file, index) => {
          const response = await putPresignedUrl({
            file,
            url: urls[index].url,
          });
          return response;
        })
      );
      console.log(uploads);
    } catch (error) {
      console.error("Error uploading files:", error);
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      return;
    }

    try {
      if (data.type === "lighting") {
        posthog.capture("analyze_lighting_form");
      } else if (data.type === "temperature") {
        posthog.capture("analyze_temperature_form");
      } else if (data.type === "noise-dose") {
        posthog.capture("analyze_noise_dose_form");
      }
      const [blob, interval] = await Promise.all([
        data.type === "lighting"
          ? extractLightingData({ imageUrls: urls.map((url) => url.key) })
          : data.type === "temperature"
          ? extractTemperatureData({ imageUrls: urls.map((url) => url.key) })
          : extractNoiseDoseData({ imageUrls: urls.map((url) => url.key) }),
        incProgress(),
      ]);
      clearInterval(interval);
      setAnalysisProgress(100);

      // Create a URL for the blob
      const url = URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = url;

      // Set the file name for download
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, "-");
      link.download = `datos-extraidos-${timestamp}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalysisProgress(0);
      }, 500);
    } catch (error) {
      console.error("Error extracting data:", error);
      setIsAnalyzing(false);
      setAnalysisProgress(0);
    }
  };

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-end gap-6">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">Capturar Datos </h1>
          <p className="text-xs text-muted-foreground">
            Utiliza el agente de inteligencia artificial para capturar los datos
            de las imagenes a formato Excel.
          </p>
        </div>
      </div>
      <Separator />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid gap-6 w-full"
        >
          <div className="flex justify-between items-center">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      className="flex gap-3"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormLabel
                          data-active={field.value === "lighting"}
                          className="h-9 w-24 rounded-lg bg-accent/10 hover:bg-accent/20 flex items-center justify-center data-[active=true]:bg-accent/30 data-[active=true]:border-primary border-2"
                        >
                          <FormControl>
                            <RadioGroupItem
                              value="lighting"
                              className="sr-only"
                            />
                          </FormControl>
                          Iluminación
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormLabel
                          data-active={field.value === "noise-dose"}
                          className="h-9 w-24 rounded-lg bg-accent/10 hover:bg-accent/20 flex items-center justify-center data-[active=true]:bg-accent/30 data-[active=true]:border-primary border-2"
                        >
                          <FormControl>
                            <RadioGroupItem
                              value="noise-dose"
                              className="sr-only"
                            />
                          </FormControl>
                          Ruido Dosis
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormLabel
                          data-active={field.value === "temperature"}
                          className="h-9 w-24 rounded-lg bg-accent/10 hover:bg-accent/20 flex items-center justify-center data-[active=true]:bg-accent/30 data-[active=true]:border-primary border-2"
                        >
                          <FormControl>
                            <RadioGroupItem
                              value="temperature"
                              className="sr-only"
                            />
                          </FormControl>
                          Temperatura
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-[2px]">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="success">
                    Descargar Formularios <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 font-mono">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a
                        href="/pdf-templates/Datos-Administrativos.pdf"
                        download
                        className="text-xs"
                      >
                        <Download className="size-4 mr-2" /> Datos
                        Administrativos
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a
                        href="/pdf-templates/Calibraciones.pdf"
                        download
                        className="text-xs"
                      >
                        <Download className="size-4 mr-2" /> Calibraciones
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a
                        href="/pdf-templates/Iluminacion-Reconocimiento.pdf"
                        download
                        className="text-xs"
                      >
                        <Download className="size-4 mr-2" /> Reco. de
                        Iluminación
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a
                        href="/pdf-templates/Iluminacion-Evaluacion.pdf"
                        download
                        className="text-xs"
                      >
                        <Download className="size-4 mr-2" /> Eval. de
                        Iluminación
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a
                        href="/pdf-templates/Ruido-Dosis-Reconocimiento.pdf"
                        download
                        className="text-xs"
                      >
                        <Download className="size-4 mr-2" /> Reco. de Ruido
                        Dosis
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a
                        href="/pdf-templates/Ruido-Dosis-Evaluacion.pdf"
                        download
                        className="text-xs"
                      >
                        <Download className="size-4 mr-2" /> Eval. de Ruido
                        Dosis
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a href="/pdf-templates/Temperatura-Reconocimiento.pdf" download className="text-xs">
                        <Download className="size-4 mr-2" /> Reco. de TGBH
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a href="/pdf-templates/Temperatura-Evaluacion.pdf" download className="text-xs">
                        <Download className="size-4 mr-2" /> Eval. de TGBH
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none"
                      asChild
                    >
                      <a href="/pdf-templates/Temperatura-Evaluacion-Corporal.pdf" download className="text-xs">
                        <Download className="size-4 mr-2" /> Eval. de TGBH Corporal
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <FormField
            control={form.control}
            name="fileInput"
            render={() => (
              <FormItem>
                <FormControl>
                  <div
                    {...getRootProps()}
                    className="w-full h-[250px] border-2 border-dashed rounded-md bg-accent/10 border-accent cursor-pointer"
                  >
                    <input {...getInputProps()} />
                    <div className="flex flex-col items-center justify-center h-full text-center w-full">
                      {isProcessing ? (
                        <>
                          <div className="flex items-center gap-2 mb-6">
                            <Loader2 className="size-10 text-primary/60 animate-spin" />
                          </div>
                          <p className="text-sm font-medium text-foreground/80 mb-2">
                            Procesando archivos... ({Math.round((filesProcessed / filesToProcess) * 100)}%)
                          </p>
                          <p className="text-xs text-muted-foreground/80">
                            Se están convirtiendo los archivos a imágenes
                          </p>
                        </>
                      ) : isAnalyzing ? (
                        <>
                          <div className="flex items-center gap-2 mb-6">
                            <Loader2 className="size-10 text-primary/60 animate-spin" />
                          </div>
                          <p className="text-sm font-medium text-foreground/80 mb-2">
                            Analizando imágenes... ({Math.round(analysisProgress)}%)
                          </p>
                          <p className="text-xs text-muted-foreground/80">
                            {analysisProgress > 99
                              ? "Preparando archivo excel para descarga..."
                              : analysisProgress > 80
                              ? "Verificando que el agente haya producido un archivo del formato correcto"
                              : analysisProgress > 70
                              ? "Verificando que los campos guardados sean los mismos de las instrucciones"
                              : analysisProgress > 50
                              ? "Guardando datos en el archivo excel"
                              : analysisProgress > 30
                              ? "Identificando los campos importantes en las imágenes"
                              : analysisProgress > 20
                              ? "Leyendo las imágenes"
                              : analysisProgress > 10
                              ? "Agente de inteligencia artificial está extrayendo los datos de las imágenes"
                              : analysisProgress > 5
                              ? "Dando instrucciones al agente de inteligencia artificial"
                              : "Preparando imágenes para el agente de inteligencia artificial"}
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-6">
                            <BrainCircuit className="size-10 text-primary/60" />
                            <p className="text-4xl font-bold text-primary/60">Capturar Datos</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Upload className="size-4 text-foreground/80" />
                            <p className="text-sm font-medium text-foreground/80">
                              {isDragActive
                                ? "Suelte los archivos aquí"
                                : "Arrastre los archivos aquí o haga clic para seleccionar"}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            El agente de inteligencia artificial tiene mejor
                            precisión con imágenes de alta resolución.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {(() => {
            const watchedFile = form.watch("fileInput");
            if (
              !isAnalyzing &&
              watchedFile instanceof Array &&
              watchedFile.length > 0
            ) {
              return (
                <ScrollArea className="h-[300px]">
                  <table className="w-full caption-bottom text-sm">
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow className="hover:bg-accent/5">
                        <TableHead className="w-full">Nombre</TableHead>
                        <TableHead className="text-right w-min">
                          Tamaño
                        </TableHead>
                        <TableHead className="text-right w-min">
                          
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.from(watchedFile).map((file, i) => (
                        <TableRow className="hover:bg-accent/5" key={i}>
                          <TableCell className="w-full">{file.name}</TableCell>
                          <TableCell className="text-right w-min font-mono">
                            {(Number(file.size ?? 0) / (1000 * 1000)).toFixed(
                              2
                            )}{" "}
                            mb
                          </TableCell>
                          <TableCell className="text-right w-min">
                            
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter className="bg-accent/20">
                      <TableRow className="hover:bg-accent/25">
                        <TableCell>Total</TableCell>
                        <TableCell className="font-mono">
                          {(
                            watchedFile.reduce(
                              (acc, file) => acc + file.size,
                              0
                            ) /
                            (1000 * 1000)
                          ).toFixed(2)}{" "}
                          mb
                        </TableCell>
                        <TableCell className="text-right font-mono" colSpan={2}>
                          
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </table>
                </ScrollArea>
              );
            }
            return null;
          })()}
          <div className="flex gap-2 w-full justify-between">
            <Tooltip>
              <TooltipTrigger>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => form.reset()}
                  disabled={form.formState.isSubmitting || !form.watch("fileInput") || (form.watch("fileInput") as File[])?.length === 0}
                  className="gap-2"
                >
                  <RotateCcw className="size-4" />
                  Resetear
                </Button>
              </TooltipTrigger>
              {(!form.watch("fileInput") || (form.watch("fileInput") as File[])?.length === 0) && <TooltipContent>
                <p>Selecciona al menos un archivo para resetear formulario</p>
              </TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger>
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting || !form.watch("fileInput") || (form.watch("fileInput") as File[])?.length === 0}
                  className="gap-2"
                >
                  {form.formState.isSubmitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <BrainCircuit className="size-4" />
                  )}
                  {form.formState.isSubmitting ? "Procesando..." : "Capturar Datos"}
                </Button>
              </TooltipTrigger>
              {(!form.watch("fileInput") || (form.watch("fileInput") as File[])?.length === 0) && <TooltipContent>
                <p>Selecciona un tipo de agente y al menos un archivo para capturar datos</p>
              </TooltipContent>}
            </Tooltip>
          </div>
        </form>
      </Form>
    </div>
  );
}
